import { useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Period, SocialAdsAnalytics_QueryFragment } from '@frontend/gql/social/graphql';
import { SOCIAL_POST_APP_ID } from '@frontend/app/constants/applicationIds';
import { DateType } from '../../Analytics/types';
import type { SocialAccountData } from '../components/social-accounts-table/social-accounts-table-types';
import type { TopAdData } from '../components/ads-table/ads-table-types';

// Types for the data we'll be processing
interface ProcessedData {
  currentInterval: SocialAdsAnalytics_QueryFragment['client']['currentInterval'];
  previousInterval: SocialAdsAnalytics_QueryFragment['client']['previousInterval'];
  currentIntervalNotAspireSourced: SocialAdsAnalytics_QueryFragment['client']['currentIntervalNotAspireSourced'];
  previousIntervalNotAspireSourced: SocialAdsAnalytics_QueryFragment['client']['previousIntervalNotAspireSourced'];
  currentIntervalsIntervalInsights: SocialAdsAnalytics_QueryFragment['client']['currentIntervalsIntervalInsights'];
  currentIntervalsIntervalInsightsNotAspireSourced: SocialAdsAnalytics_QueryFragment['client']['currentIntervalsIntervalInsightsNotAspireSourced'];
  currentIntervalRange: {
    precision: Period;
  };
  topAds: TopAdData[];
  topSocialAccounts: SocialAccountData[];
  currency: string;
  metaAdAccounts: SocialAdsAnalytics_QueryFragment['client']['metaAdAccounts'];
}

interface QueryVariables {
  showNonAttributedData: boolean;
  fromOldData: boolean;
  startDate: string;
  endDate: string;
  dateType: DateType;
}

interface UseSocialAdsOverviewTabProps {
  // Raw data from other hooks
  processedData: ProcessedData;
  queryVariables: QueryVariables;
  isLoading: boolean;
  // Event handlers from parent
  onShowNonAttributedDataChange: (checked: boolean) => void;
  onFromOldDataChange: (checked: boolean) => void;
  handleTabChange: (tab: string) => void;
}

interface SocialAdsOverviewTabProps {
  className?: string;
  // Data props
  isLoading: boolean;
  currentInterval: SocialAdsAnalytics_QueryFragment['client']['currentInterval'];
  previousInterval: SocialAdsAnalytics_QueryFragment['client']['previousInterval'];
  currentIntervalNotAspireSourced: SocialAdsAnalytics_QueryFragment['client']['currentIntervalNotAspireSourced'];
  previousIntervalNotAspireSourced: SocialAdsAnalytics_QueryFragment['client']['previousIntervalNotAspireSourced'];
  currentIntervalsIntervalInsights: SocialAdsAnalytics_QueryFragment['client']['currentIntervalsIntervalInsights'];
  currentIntervalsIntervalInsightsNotAspireSourced: SocialAdsAnalytics_QueryFragment['client']['currentIntervalsIntervalInsightsNotAspireSourced'];
  currentIntervalRange: {
    precision: Period;
  };
  topAds: TopAdData[];
  topSocialAccounts: SocialAccountData[];
  currency: string;
  // Query variables
  queryVariables: QueryVariables;
  // Alert states
  hasNoMetaAdAccounts: boolean;
  hasDisconnectedMetaAdAccounts: boolean;
  // Event handlers
  onShowNonAttributedDataChange: (checked: boolean) => void;
  onFromOldDataChange: (checked: boolean) => void;
  onConnectInstagram: () => void;
  onReconnectAccount: () => void;
  // Helper function
  getPrecisionLabel: (precision: Period) => string;
  handleTabChange: (tab: string) => void;
}

interface UseSocialAdsOverviewTabReturn {
  socialAdsOverviewTabProps: SocialAdsOverviewTabProps;
}

/**
 * Hook that processes data and returns props for SocialAdsOverview component
 * This hook encapsulates all the logic needed to transform raw data into
 * the format expected by the SocialAdsOverview component
 */
export const useSocialAdsOverviewTab = ({
  processedData,
  queryVariables,
  isLoading,
  onShowNonAttributedDataChange,
  onFromOldDataChange,
  handleTabChange,
}: UseSocialAdsOverviewTabProps): UseSocialAdsOverviewTabReturn => {
  const history = useHistory();

  // Extract data from processedData
  const {
    currentInterval,
    previousInterval,
    currentIntervalNotAspireSourced,
    previousIntervalNotAspireSourced,
    currentIntervalsIntervalInsights,
    currentIntervalsIntervalInsightsNotAspireSourced,
    currentIntervalRange,
    topAds,
    topSocialAccounts,
    currency,
    metaAdAccounts,
  } = processedData;

  // Alert logic helpers - memoized for performance
  const hasDisconnectedMetaAdAccounts = useMemo(() => {
    return (
      !isLoading &&
      metaAdAccounts &&
      metaAdAccounts.length > 0 &&
      metaAdAccounts.some((account) => account?.status !== 'ACTIVE')
    );
  }, [isLoading, metaAdAccounts]);

  const hasNoMetaAdAccounts = useMemo(() => {
    return !isLoading && (!metaAdAccounts || metaAdAccounts.length === 0);
  }, [isLoading, metaAdAccounts]);

  // Alert handlers
  const handleConnectInstagram = useCallback(() => {
    // Handle connect Instagram account action
    history.push(`/settings/${SOCIAL_POST_APP_ID}`);
  }, [history]);

  const handleReconnectAccount = useCallback(() => {
    // Handle reconnect account action
    history.push(`/settings/${SOCIAL_POST_APP_ID}`);
  }, [history]);

  // Helper function to get precision-based labels
  const getPrecisionLabel = useCallback((precision: Period) => {
    switch (precision) {
      case 'DAY':
        return 'Daily';
      case 'QUARTER':
        return 'Quarterly';
      case 'MONTH':
      default:
        return 'Monthly';
    }
  }, []);

  // Create the socialAdsOverviewProps object
  const socialAdsOverviewTabProps: SocialAdsOverviewTabProps = {
    isLoading,
    currentInterval,
    previousInterval,
    currentIntervalNotAspireSourced,
    previousIntervalNotAspireSourced,
    currentIntervalsIntervalInsights,
    currentIntervalsIntervalInsightsNotAspireSourced,
    currentIntervalRange,
    topAds,
    topSocialAccounts,
    currency,
    queryVariables,
    hasNoMetaAdAccounts,
    hasDisconnectedMetaAdAccounts,
    onShowNonAttributedDataChange,
    onFromOldDataChange,
    onConnectInstagram: handleConnectInstagram,
    onReconnectAccount: handleReconnectAccount,
    getPrecisionLabel,
    handleTabChange,
  };

  // Return the object containing socialAdsOverviewProps
  return {
    socialAdsOverviewTabProps,
  };
};

export type { SocialAdsOverviewTabProps, ProcessedData, QueryVariables, UseSocialAdsOverviewTabReturn, TopAdData };
